export interface ContactInfo {
  name: string;
  relationship?: string;
  phone?: string;
  email?: string;
}

export interface PhysicianInfo {
  name: string;
  phone?: string;
  address?: string;
}

export interface InsuranceInfo {
  provider?: string;
  policyNumber?: string;
  groupNumber?: string;
}

// Stored under students.metadata.bhcs_waiver when signed
export interface BhcsStudentWaiver {
  parent1: ContactInfo;
  parent2?: ContactInfo;
  emergencyContact: ContactInfo;
  hasAllergies: boolean;
  allergiesDetails?: string;
  physician?: PhysicianInfo;
  insurance?: InsuranceInfo;
  consents: {
    emergencyMedicalAuthorization: boolean;
    liabilityWaiver: boolean;
    assumptionOfRisk: boolean;
    behaviorPolicy: boolean;
    photoRelease: boolean; // true = permission given, false = do NOT give permission
    infectiousDiseaseDisclaimer: boolean;
  };
  signature: {
    printedName: string;
    signature: string;
    date: string; // YYYY-MM-DD
    fontStack?: string; // optional, for rendering the signature in the chosen font
  };
  submittedAt: string; // ISO timestamp
  // Set by update_student_waiver_contacts() whenever a guardian/emergency
  // contact edit is saved post-signing. Distinct from submittedAt, which
  // stays pinned to the original waiver attestation.
  contactsUpdatedAt?: string; // ISO timestamp
}

// Keep this in sync with the default signature font used in `StudentWaiver`
export const DEFAULT_BHCS_WAIVER_SIGNATURE_FONT_STACK = '"Snell Roundhand", cursive';

// Stored under students.metadata.bhcs_waiver when opted out (transition support)
export interface BhcsStudentWaiverOptOut {
  opt_out: true;
  optedOutAt?: string; // ISO timestamp, optional
  [key: string]: unknown;
}

export type BhcsWaiverMetadata = BhcsStudentWaiver | BhcsStudentWaiverOptOut;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function isBhcsStudentWaiverOptOut(value: unknown): value is BhcsStudentWaiverOptOut {
  if (!isRecord(value)) return false;
  return value.opt_out === true;
}

export interface WaiverContacts {
  parent1?: ContactInfo;
  parent2?: ContactInfo;
  emergencyContact?: ContactInfo;
}

function readContact(value: unknown): ContactInfo | undefined {
  if (!isRecord(value)) return undefined;
  const name = typeof value.name === 'string' ? value.name : '';
  const relationship = typeof value.relationship === 'string' ? value.relationship : undefined;
  const phone = typeof value.phone === 'string' ? value.phone : undefined;
  const email = typeof value.email === 'string' ? value.email : undefined;
  if (!name && !relationship && !phone && !email) return undefined;
  return { name, relationship, phone, email };
}

// Reads the guardian / emergency-contact subtree out of ANY bhcs_waiver shape:
// signed, opted-out, or a contacts-only object written by
// update_student_waiver_contacts() onto a row that was never signed.
//
// Deliberately does NOT route through isBhcsStudentWaiverSigned. Contacts are
// editable on opt-out rows (102 of 282 students in prod are opt-out), and that
// guard returns false for anything carrying opt_out:true — so gating the read
// on a signature would let a parent save contacts and then see the form come
// back empty, with the contacts rendering nowhere.
export function readWaiverContacts(value: unknown): WaiverContacts {
  if (!isRecord(value)) return {};
  return {
    parent1: readContact(value.parent1),
    parent2: readContact(value.parent2),
    emergencyContact: readContact(value.emergencyContact),
  };
}

export function isBhcsStudentWaiverSigned(value: unknown): value is BhcsStudentWaiver {
  if (!isRecord(value)) return false;
  if (isBhcsStudentWaiverOptOut(value)) return false;

  const signature = value.signature;
  if (!isRecord(signature)) return false;

  // Minimal shape checks needed for rendering/printing safely
  return (
    typeof (value.submittedAt) === 'string' &&
    typeof (signature.printedName) === 'string' &&
    typeof (signature.signature) === 'string' &&
    typeof (signature.date) === 'string'
  );
}


